import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { KeyRound, TriangleAlert } from 'lucide-react';
import type { Role } from '@trellis/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Card, cx } from '../components/ui';

/**
 * Accesos rápidos de desarrollo.
 *
 * Aparecen sólo si la API arrancó con `DEV_AUTH_BYPASS=true`. El servidor es la
 * única fuente de verdad sobre eso: el front se limita a preguntarle. Así no hay
 * forma de que una variable del build deje estos botones colgados en un entorno
 * donde el bypass no existe — como mucho no harían nada.
 */

export interface AuthConfig {
  entraEnabled: boolean;
  devAuthBypass: boolean;
}

export function useAuthConfig() {
  return useQuery({
    queryKey: ['auth', 'config'],
    queryFn: () => api.get<AuthConfig>('/auth/config'),
    // No cambia durante la sesión: se pide una vez y no se vuelve a pedir.
    staleTime: Infinity,
    /**
     * Reintenta. De esta respuesta depende que aparezca el botón de Microsoft,
     * y sin reintento un hipo de red —o la API todavía levantando después de un
     * despliegue— dejaba a Dx Tech sin forma de entrar hasta recargar la página
     * entera, sin ningún mensaje que lo explicara.
     */
    retry: 3,
    retryDelay: (intento) => Math.min(1000 * 2 ** intento, 8000),
  });
}

interface DevUser {
  username: string;
  fullName: string | null;
  role: Role;
}

const ROLE_LABELS: Record<string, string> = {
  SUPERADMIN: 'Administración Dx Tech',
  PHARMA_ADMIN: 'Administración de la institución',
  CASE_MANAGER: 'Equipo de seguimiento',
  PATIENT: 'Paciente',
  CAREGIVER: 'Cuidador',
};

/** Orden en el que conviene ofrecerlos: de menos a más privilegios. */
const ROLE_ORDER: Role[] = ['PATIENT', 'CASE_MANAGER', 'PHARMA_ADMIN', 'SUPERADMIN'];

export function DevQuickLogin({ slug, redirectTo }: { slug: string | null; redirectTo?: string }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const config = useAuthConfig();

  const users = useQuery({
    queryKey: ['auth', 'dev-users', slug],
    queryFn: () => api.get<{ users: DevUser[] }>(`/auth/dev-users${slug ? `?slug=${slug}` : ''}`),
    enabled: config.data?.devAuthBypass === true,
    staleTime: 60_000,
  });

  if (!config.data?.devAuthBypass) return null;

  const available = users.data?.users ?? [];
  const byRole = ROLE_ORDER.map((role) => ({
    role,
    users: available.filter((user) => user.role === role),
  })).filter((group) => group.users.length > 0);

  const enter = async (username: string, role: Role): Promise<void> => {
    const result = await login(slug, username, '', role);
    if (redirectTo) {
      navigate(redirectTo, { replace: true });
      return;
    }
    // El bypass entra por un usuario concreto, nunca deja opciones que elegir.
    if (result.kind !== 'session') return;

    const { role: entered } = result.user;
    navigate(entered === 'PATIENT' || entered === 'CAREGIVER' ? `/${slug}/app` : `/${slug}/console`, {
      replace: true,
    });
  };

  return (
    <Card className="p-4 mt-4 border-warning bg-warning-soft">
      <div className="flex items-center gap-2 mb-1">
        <TriangleAlert className="size-4 text-warning shrink-0" />
        <p className="font-semibold text-sm">Modo desarrollo</p>
      </div>
      <p className="text-[13px] text-fg-muted leading-snug mb-3">
        La contraseña no se verifica. Entrá con cualquier valor, o elegí un usuario:
      </p>

      {byRole.length === 0 ? (
        <p className="text-[13px] text-fg-muted">
          No hay usuarios sembrados. Corré <code className="font-mono">npm run seed</code>.
        </p>
      ) : (
        <div className="space-y-3">
          {byRole.map((group) => (
            <div key={group.role}>
              <p className="text-[11px] uppercase tracking-wide text-fg-subtle font-semibold mb-1.5">
                {ROLE_LABELS[group.role] ?? group.role}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {group.users.slice(0, 6).map((user) => (
                  <button
                    key={user.username}
                    onClick={() => void enter(user.username, group.role)}
                    className={cx(
                      'px-2.5 py-1.5 rounded-md text-[13px] font-medium',
                      'bg-surface-raised border border-border hover:border-primary hover:text-primary',
                      'transition-colors text-left',
                    )}
                  >
                    {user.fullName ?? user.username}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/**
 * Cartel fijo que recuerda que la autenticación está desactivada.
 *
 * Está siempre a la vista, en todas las pantallas y para todos los roles. Es
 * incómodo, y esa es la idea: la peor forma de mostrarle Trellis a un
 * institución sería con el bypass prendido sin darse cuenta.
 */
export function DevBypassBadge() {
  const config = useAuthConfig();

  if (!config.data?.devAuthBypass) return null;

  return (
    <div
      className="fixed bottom-2 left-2 z-[60] pointer-events-none select-none"
      role="status"
      aria-label="La autenticación está desactivada en este entorno"
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-warning text-white px-2.5 py-1 text-[11px] font-bold shadow-md">
        <KeyRound className="size-3" />
        SIN AUTENTICACIÓN
      </span>
    </div>
  );
}

/** Botón para entrar sin tipear nada, para las pantallas que sólo tienen formulario. */
export function DevSkipButton({ slug, redirectTo }: { slug: string | null; redirectTo: string }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const config = useAuthConfig();

  if (!config.data?.devAuthBypass) return null;

  return (
    <Button
      variant="outline"
      fullWidth
      onClick={async () => {
        await login(slug, '', '');
        navigate(redirectTo, { replace: true });
      }}
    >
      Entrar sin credenciales
    </Button>
  );
}
